import { Formatters } from '../lib/formatters';
import { isVideoUrl } from '../lib/mediaKind';   // R116 #5: the one shared rule (was a private copy)

// R112 (Jesse #6: "if I click on one photo, I would be able to scroll through
// the rest of the photos"): ONE shared full-screen media viewer for every
// thumb strip in the app. A strip hands it the whole url list + the tapped
// index; the viewer pages with ‹ › buttons, ← → keys, a touch swipe, shows
// "3 / 12", closes on Esc / backdrop / ✕, renders a video for /video/ urls,
// preloads the neighbours, and never leaves a blank box: a photo that fails
// to load (deleted object, HEIC on a non-Apple browser) says so in place.
//
// It lives on document.body (outside every page container), so it binds its
// own listeners and cleans them up on close. One instance at a time.
// R116 #5: it now opens from strips INSIDE modals (the Issues drawer 9500, the
// quick-log / WO quick view 9000), so it sits above every overlay (10000).

export interface LightboxOpts {
  /** Caption under the media for index i (already-safe text; escaped here). */
  caption?: (i: number) => string;
  /** Starting index (defaults to the `index` argument). */
  start?: number;
}

const CSS_ID = 'sx-lightbox-css';
const CSS = `
.sx-lb { position:fixed; inset:0; z-index:10000; background:rgba(15,23,42,0.92); display:flex; flex-direction:column; align-items:center; justify-content:center; touch-action:pan-y; user-select:none; -webkit-user-select:none; }
.sx-lb-stage { position:relative; flex:1; width:100%; display:flex; align-items:center; justify-content:center; min-height:0; padding:12px 56px; box-sizing:border-box; }
.sx-lb-stage img, .sx-lb-stage video { max-width:100%; max-height:calc(100vh - 110px); max-height:calc(100dvh - 110px); border-radius:var(--radius-sm, 8px); background:var(--primary, #0f172a); box-shadow:0 12px 40px rgba(0,0,0,0.45); object-fit:contain; }
.sx-lb-broken { display:flex; flex-direction:column; align-items:center; gap:8px; padding:36px 28px; border-radius:var(--radius, 16px); background:rgba(255,255,255,0.08); color:#fff; font:600 14px/1.4 var(--font-sans, system-ui, sans-serif); text-align:center; max-width:80vw; }
.sx-lb-broken small { font-weight:400; opacity:0.75; font-size:12px; word-break:break-all; }
.sx-lb-btn { position:absolute; top:50%; transform:translateY(-50%); width:44px; height:44px; border-radius:999px; border:none; background:rgba(255,255,255,0.14); color:#fff; font-size:26px; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .12s ease; }
.sx-lb-btn:hover { background:rgba(255,255,255,0.28); }
.sx-lb-btn[disabled] { opacity:0.25; cursor:default; }
.sx-lb-prev { left:8px; } .sx-lb-next { right:8px; }
.sx-lb-x { position:absolute; top:10px; right:10px; width:40px; height:40px; border-radius:999px; border:none; background:rgba(255,255,255,0.14); color:#fff; font-size:20px; cursor:pointer; z-index:2; }
.sx-lb-x:hover { background:rgba(255,255,255,0.28); }
.sx-lb-bar { flex:none; display:flex; align-items:center; justify-content:center; gap:14px; padding:8px 16px 14px; padding-bottom:calc(14px + env(safe-area-inset-bottom, 0px)); color:#fff; font:600 13px/1.3 var(--font-sans, system-ui, sans-serif); max-width:100%; }
.sx-lb-count { font-variant-numeric:tabular-nums; opacity:0.9; background:rgba(255,255,255,0.12); border-radius:999px; padding:3px 10px; }
.sx-lb-cap { font-weight:500; opacity:0.85; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:70vw; }
.sx-lb-open { font-weight:500; color:#fff; opacity:0.75; text-decoration:underline; font-size:12px; }
@media (max-width: 640px) { .sx-lb-stage { padding:8px 4px; } .sx-lb-btn { width:40px; height:40px; background:rgba(255,255,255,0.1); } .sx-lb-prev { left:4px; } .sx-lb-next { right:4px; } }
`;

const isHeicUrl = (u: string) => /\.hei[cf](\?|#|$)/i.test(u);

let current: { close: () => void } | null = null;

/** Open the viewer on `urls[index]`. Photos and /video/ urls can be mixed. */
export function openLightbox(urls: string[], index = 0, opts: LightboxOpts = {}): void {
  const list = (urls || []).map(u => String(u || '')).filter(Boolean);
  if (!list.length) return;
  if (current) current.close();
  if (!document.getElementById(CSS_ID)) {
    const st = document.createElement('style');
    st.id = CSS_ID;
    st.textContent = CSS;
    document.head.appendChild(st);
  }
  let i = Math.max(0, Math.min(list.length - 1, Number.isFinite(opts.start as number) ? Number(opts.start) : index));
  const esc = (s: any) => Formatters.escapeHtml(s == null ? '' : String(s));
  const box = document.createElement('div');
  box.className = 'sx-lb';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', 'Photo viewer');
  box.innerHTML = `
    <button type="button" class="sx-lb-x" data-lb-x title="Close (Esc)" aria-label="Close">✕</button>
    <div class="sx-lb-stage" data-lb-stage>
      <button type="button" class="sx-lb-btn sx-lb-prev" data-lb-prev title="Previous (←)" aria-label="Previous">‹</button>
      <div data-lb-media style="display:flex; align-items:center; justify-content:center; max-width:100%; max-height:100%;"></div>
      <button type="button" class="sx-lb-btn sx-lb-next" data-lb-next title="Next (→)" aria-label="Next">›</button>
    </div>
    <div class="sx-lb-bar">
      <span class="sx-lb-count" data-lb-count></span>
      <span class="sx-lb-cap" data-lb-cap></span>
      <a class="sx-lb-open" data-lb-open target="_blank" rel="noopener">open original</a>
    </div>`;
  const media = box.querySelector('[data-lb-media]') as HTMLElement;
  const count = box.querySelector('[data-lb-count]') as HTMLElement;
  const cap = box.querySelector('[data-lb-cap]') as HTMLElement;
  const openA = box.querySelector('[data-lb-open]') as HTMLAnchorElement;
  const prev = box.querySelector('[data-lb-prev]') as HTMLButtonElement;
  const next = box.querySelector('[data-lb-next]') as HTMLButtonElement;

  const preload = (k: number) => {
    const u = list[k];
    if (!u || isVideoUrl(u)) return;
    const im = new Image();
    im.decoding = 'async';
    im.src = u;
  };
  const show = () => {
    const u = list[i];
    count.textContent = list.length > 1 ? `${i + 1} / ${list.length}` : '';
    const c = opts.caption ? String(opts.caption(i) || '') : '';
    cap.textContent = c;
    cap.style.display = c ? '' : 'none';
    openA.href = u;
    prev.disabled = i <= 0;
    next.disabled = i >= list.length - 1;
    if (isVideoUrl(u)) {
      media.innerHTML = `<video src="${esc(u)}" controls autoplay playsinline preload="metadata"></video>`;
      // R116 #5: a clip this browser can't decode (an iPhone HEVC .mov on an
      // older PC / Firefox) errors, or loads with no picture (videoWidth 0 —
      // audio only). Say so and offer the file, never a black box.
      const v = media.querySelector('video') as HTMLVideoElement;
      const noVideo = () => {
        if (list[i] !== u) return;
        media.innerHTML = `<div class="sx-lb-broken">🎥 This browser can't play this video<small>It's probably an iPhone (HEVC) clip — it plays on iPhone / Mac / recent Chrome. <a href="${esc(u)}" target="_blank" rel="noopener" download style="color:#fff; text-decoration:underline;">Download it</a> to watch.</small></div>`;
      };
      v.addEventListener('error', noVideo, { once: true });
      v.addEventListener('loadedmetadata', () => { if (!v.videoWidth) noVideo(); }, { once: true });
    } else {
      media.innerHTML = `<img src="${esc(u)}" alt="photo ${i + 1} of ${list.length}" decoding="async" draggable="false">`;
      const img = media.querySelector('img') as HTMLImageElement;
      img.addEventListener('error', () => {
        media.innerHTML = `<div class="sx-lb-broken">📷 Photo unavailable${isHeicUrl(u) ? '<small>HEIC file — this browser can\'t show it (opens on iPhone / Safari).</small>' : '<small>It may still be uploading, or the file was removed.</small>'}</div>`;
      }, { once: true });
    }
    preload(i + 1); preload(i - 1);
  };
  const go = (d: number) => {
    const n = i + d;
    if (n < 0 || n >= list.length) return;
    i = n; show();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
  };
  const prevOverflow = document.body.style.overflow;
  const close = () => {
    document.removeEventListener('keydown', onKey);
    box.remove();
    document.body.style.overflow = prevOverflow;
    if (current && current.close === close) current = null;
  };
  current = { close };
  document.addEventListener('keydown', onKey);
  document.body.style.overflow = 'hidden';
  (box.querySelector('[data-lb-x]') as HTMLElement).addEventListener('click', close);
  prev.addEventListener('click', (e) => { e.stopPropagation(); go(-1); });
  next.addEventListener('click', (e) => { e.stopPropagation(); go(1); });
  // Backdrop (anything that isn't the media itself or a control) closes.
  box.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('img, video, button, a, .sx-lb-broken')) return;
    close();
  });
  // Touch swipe — horizontal travel ≥ 40px with little vertical drift.
  // R112 review: a drag on the native <video> scrubber is a horizontal swipe
  // too (the controls' touches bubble to the box), and paging away mid-seek
  // tore the video down. Gestures that START on the video never page; the
  // backdrop and photos still swipe, and a seek in flight is never paged over.
  let sx = 0, sy = 0, swiping = false;
  const onVideo = (t: EventTarget | null) => t instanceof Element && !!t.closest('video');
  const seeking = () => { const v = media.querySelector('video'); return !!(v && v.seeking); };
  box.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1 || onVideo(e.target)) { swiping = false; return; }
    sx = e.touches[0].clientX; sy = e.touches[0].clientY; swiping = true;
  }, { passive: true });
  box.addEventListener('touchend', (e) => {
    if (!swiping) return;
    swiping = false;
    const t = e.changedTouches[0];
    if (!t || onVideo(e.target) || seeking()) return;
    const dx = t.clientX - sx, dy = t.clientY - sy;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  }, { passive: true });
  document.body.appendChild(box);
  show();
}

/** Close the open viewer, if any (pages call this before a full redraw). */
export function closeLightbox(): void { if (current) current.close(); }

/**
 * Collect the url list for a tapped thumb: every `[data-attr]` element inside
 * the nearest `[data-hk-strip]` / `[data-strip]` ancestor (else its parent),
 * in DOM order, plus the tapped one's position. Shared helper so every page
 * wires its strips the same way.
 */
export function stripUrlsFor(el: HTMLElement, attr: string): { urls: string[]; index: number } {
  const strip = (el.closest('[data-hk-strip], [data-strip]') as HTMLElement | null) || el.parentElement;
  const nodes = strip ? Array.from(strip.querySelectorAll<HTMLElement>(`[${attr}]`)) : [el];
  const urls = nodes.map(n => n.getAttribute(attr) || '').filter(Boolean);
  const index = Math.max(0, nodes.indexOf(el));
  return { urls: urls.length ? urls : [el.getAttribute(attr) || ''], index: urls.length ? index : 0 };
}
